/*
 * Copyright (c) 2022 NEXTWELL INTL SRL. All Rights Reserved.
 * This software is the proprietary information of NEXTWELL INTL SRL.
 * Use is subject to license and non-disclosure terms
 */

import { Component, EventEmitter, forwardRef, Input, Output } from '@angular/core';
import { FormControl, NG_VALIDATORS, NG_VALUE_ACCESSOR } from '@angular/forms';
import { AbstractControlValueAccessorGroup, ErrorService } from '@irian-ro/shared-components-module';
import {
  SubscriptionDtoModel,
  SubscriptionLegalEntityDtoModel,
  SubscriptionVisibilityDto, VendorDtoModel
} from '@well/domain';
import { NewSubscriptionService } from './new-subscription.service';
import { Observable } from 'rxjs';
import { ToastMessageOptions } from 'primeng/api';
import { LegalEntityModel } from '@well/common-libs/legal-entity-management/domain-model';
import { SubscriptionTypeDetailsService } from '../../../services/subscription-type-details.service';

@Component({
  selector: 'well-new-subscription',
  templateUrl: './new-subscription.component.html',
  styleUrls: ['./new-subscription.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => NewSubscriptionComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => NewSubscriptionComponent),
      multi: true
    }
  ]
})
export class NewSubscriptionComponent extends AbstractControlValueAccessorGroup {

  legalEntitySearchLocation: 'ANAF' | 'ALL' = 'ALL';

  placeholder = 'Nume sau CUI';

  legalEntity: LegalEntityModel | null = null;

  @Output() closeComponent: EventEmitter<boolean> = new EventEmitter<boolean>();

  private _subscriptionTypeUuid: string | undefined = undefined;

  vendor: VendorDtoModel = new VendorDtoModel();

  get subscriptionTypeUuid(): string | undefined {
    return this._subscriptionTypeUuid;
  }

  @Input() set subscriptionTypeUuid(value: string) {
    this._subscriptionTypeUuid = value;
    this.getSubscriptionTypeUuidControl().reset(this._subscriptionTypeUuid);
  }

  msg: Observable<ToastMessageOptions[]> | undefined = undefined;

  constructor(private newSubscriptionService: NewSubscriptionService,
              private errorService: ErrorService,
              private subscriptionTypeDetailsService: SubscriptionTypeDetailsService) {
    super();
  }

  onCompanySelected(legalEntityModel: LegalEntityModel) {
    const legalEntity = {
      uuid: legalEntityModel.uuid,
      companyName: legalEntityModel.companyName,
      cui: legalEntityModel.uniqueIdNumber
    };
    this.getLegalEntityControl().reset(legalEntity);
    this.legalEntity = legalEntityModel;
    this.save();
  }

  goBack() {
    this.closeComponent.next(true);
  }

  save() {
    console.log(this.legalEntity);
    if(this.legalEntity?.uuid == null){
      this.saveLegalEntityWithVisibility();
    }
    else{
      if(!LegalEntityModel.isInPlatform(this.legalEntity)){
        this.saveVisibility();
      }
      else{
        this.saveSubscription();
      }
    }
  }

  private saveSubscription() {

    const subscriptionDto = this.getSubscriptionDto();

    if (this.legalEntity != null && this.subscriptionTypeUuid != null) {
      this.newSubscriptionService.onSave(subscriptionDto)
        .subscribe(
          () => {
            if(this.subscriptionTypeUuid) {
              this.subscriptionTypeDetailsService.getOne(this.subscriptionTypeUuid)
                .subscribe(
                )
              this.goBack();
            }
          },
          (error: any) => {
            this.msg = this.errorService.prepareErrorsForDisplay(error, 'company.actions.errors');

          }
        );
    }
  }

  private saveVisibility() {
    this
      .newSubscriptionService
      .saveVisibility(this.getSubscriptionVisibilityDto())
      .subscribe(() => {
        this.goBack();
      });
  }

  private saveLegalEntityWithVisibility() {
    this
      .newSubscriptionService
      .saveCompanyDetailsAndAddSubscriptionVisibility(this.getSubscriptionVisibilityDto())
      .subscribe(() => {
        this.goBack();
      });
  }

  protected createForm() {
    this.form = SubscriptionDtoModel.createForm();
  }

  getLegalEntityControl() {
    return this.form.controls['legalEntity'] as FormControl;
  }

  getSubscriptionTypeUuidControl() {
    return this.form.controls['subscriptionTypeUuid'] as FormControl;
  }

  private getSubscriptionVisibilityDto(): SubscriptionVisibilityDto{
    const subscriptionVisibility = new SubscriptionVisibilityDto();
    subscriptionVisibility.subscriptionTypeId = this.subscriptionTypeUuid ? this.subscriptionTypeUuid : null;
    subscriptionVisibility.legalEntityDTO = this.legalEntity;

    return subscriptionVisibility;
  }

  private getSubscriptionDto(): SubscriptionDtoModel{
    const subscriptionLegalEntity = new SubscriptionLegalEntityDtoModel();
    subscriptionLegalEntity.uuid = this.legalEntity!.uuid!;
    subscriptionLegalEntity.cui = this.legalEntity!.uniqueIdNumber!;
    subscriptionLegalEntity.companyName = this.legalEntity!.companyName!;
    subscriptionLegalEntity.companyType = this.legalEntity!.companyType!;

    const subscription = new SubscriptionDtoModel();
    subscription.subscriptionTypeUuid = this.subscriptionTypeUuid;
    subscription.legalEntity = subscriptionLegalEntity;

    return subscription;
  }

}
